import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Dispatch } from "../../Dispatch";
import { ConvertVoiceChannelImportedBookingToPhoneToCnpBookingDetails } from "../ConvertApiContractsToInternalModels";
import { WellknownErrorMessages } from "./Notifications/NotificationEntities";
import { Api } from "../../../Services/Api";
import { BookingTracking } from "./BookingTracking";
import { ConsiderDisplayingNoticeAndToasterForNoJob, ConsiderDisplayingToasterForPickedUpNotCnp } from "../RefreshVoiceBooking";
import { BackingUserDetail } from "../../GuestPayment/GuestPaymentState";

/**
 * Phone booking link handler component. This is opened when the user clicks the link in the SMS they receive after making a booking through a voice channel (e.g. IVR, Contact center). This page allows users to track the status of their booking and see the details of their booking. This page also allows the user to convert the booking in to a Fixed Price booking and pay with a digital payment method (e.g.: 16 digit card, ApplePay, GPay etc).
 */
export const PhoneBookingLinkHandler: React.FC = () => {
    const location = useLocation();
    
    // Extract query parameters from URL
    const queryParams = new URLSearchParams(location.search);
    const dispatchBookingId = queryParams.get('DispatchBookingId');
    const dispatchSystemId = queryParams.get('DispatchSystemId');
    const bookingAccessCode = queryParams.get('BookingAccessCode');

    useEffect(() => {

        if (dispatchBookingId && dispatchSystemId && bookingAccessCode) { 
            // Call the API endpoint
            fetchBookingDetails(dispatchBookingId, dispatchSystemId, bookingAccessCode);
        } else {
            // Show error message when required parameters are missing
            Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.GenericMessage);
        }
    }, [dispatchBookingId, dispatchSystemId, bookingAccessCode]);

    return (
            <BookingTracking />
    )
}

/**
 * Fetch booking details from the API endpoint.
 */
async function fetchBookingDetails(
    dispatchBookingId: string,
    dispatchSystemId: string,
    bookingAccessCode: string
): Promise<void> {
    
    // Set loading state to true
    Dispatch.PhoneToCnp.StartLoadingBookingDetails();
    
    try {
        const request = {
            DispatchBookingId: Number(dispatchBookingId),
            DispatchSystemId: Number(dispatchSystemId),
            BookingAccessCode: bookingAccessCode
        };
        
        const result = await Api.Booking.ImportBookingFromVoiceChannel(request);
        
        if (result.isSuccess && result.value) {
            const bookingDetails = ConvertVoiceChannelImportedBookingToPhoneToCnpBookingDetails(result.value);
            Dispatch.PhoneToCnp.RefreshBookingDetails(bookingDetails);
            // Clear any existing error messages on successful load
            Dispatch.PhoneToCnp.HideErrorMessage();

            // If the backend returned guest user auth credentials (for a return user with a saved card),
            // set up the BackingUser so ServiceManager can add the Authorization header to API calls
            // (e.g. when deleting the saved card or updating the booking with payment).
            if (result.value.GuestUserAuth) {
                const { Email, Password } = result.value.GuestUserAuth;
                const backingUser: BackingUserDetail = {
                    Auth: 'Basic ' + btoa(Email + ':' + Password),
                    PhoneNumber: result.value.CustomerPhone
                };
                Dispatch.GuestPayment.TempUserDetails(backingUser);

                // Also store the credentials in the PhoneToCnp slice so ExistingCardDropdownEntry
                // can always authenticate DeleteCard with the correct guest account credentials,
                // even if GuestPayment.BackingUser is later overwritten by another payment flow.
                // This is exclusively for the phone-to-CNP flow and has no impact on other booking flows.
                Dispatch.PhoneToCnp.SetSavedCardGuestAuth(backingUser);
            }

            // Display notices based on the status if applicable
            ConsiderDisplayingNoticeAndToasterForNoJob(bookingDetails, bookingDetails.TrackingInfo.BookingStatusID);
            ConsiderDisplayingToasterForPickedUpNotCnp(bookingDetails, bookingDetails.TrackingInfo.BookingStatusID);
        } else {
            Dispatch.PhoneToCnp.ShowErrorMessage(WellknownErrorMessages.GenericMessage);
        }
    } finally {
        // Set loading state to false when API call completes
        Dispatch.PhoneToCnp.StopLoadingBookingDetails();
    }
}
